import {dataResponseConverter, Endpoint, MakeRequest} from './request'
import type {Secure} from '../helpers/userSecure'
import type {
  AdvertiseHash,
  AppStorage,
  CAParamContent,
  CAParamsModel,
  CreateAdvertiseModel,
  DraftModel,
  Unknown,
} from '../index'

interface SaveDraftMethodParams {
  readonly draft: CAParamsModel
  readonly hash?: string | null
}

export class CreateAdvertiseApi {
  constructor(
    private request: MakeRequest,
    private storage: AppStorage,
    private secure: Secure,
  ) {}

  async sendOtherValue(fieldId: number, value: string) {
    return await this.request.requestData({
      api: Endpoint.fieldOfferValue,
      method: 'POST',
      body: {
        hash: this.storage.userHash,
        secur: this.secure.createUserSecure(),
        lang: this.storage.language,
        field_id: fieldId.toString(),
        field_text: value,
      },
    })
  }

  fetchListDrafts = async (params: {
    limit: number
    page: number
    cacheId?: string
  }) => {
    return await this.request.newRequest<DraftModel[]>(
      {
        endpoint: 'v3/drafts',
        data: params.cacheId ? {cacheId: params.cacheId} : undefined,
        bodyHeaders: {
          pagination: {
            limit: params.limit,
            page: params.page ?? 1,
          },
        },
      },
      dataResponseConverter,
    )
  }

  fetchDraft = async (draftHash: string) => {
    return await this.request.newRequest<DraftModel>(
      {
        endpoint: `v2/draft/${draftHash}`,
      },
      dataResponseConverter,
    )
  }

  saveDraft = async (params: SaveDraftMethodParams) => {
    const response = await this.request.newRequest<{hash: number}>(
      {
        endpoint: 'v2/draft/save',
        data: {
          advertDraft: params.draft,
          hash: params.hash ?? undefined,
        },
      },
      (raw) => ({hash: raw.hash}),
    )
    if (response.error) throw new Error(response.error)
    return response
  }

  deleteDraft = async (draftHashes: string[]) => {
    return await this.request.newRequest(
      {
        endpoint: 'v2/drafts/delete',
        data: {hashes: draftHashes},
      },
      dataResponseConverter,
    )
  }

  submitAdvertise = async (
    params: CAParamsModel,
    shouldUpdate: boolean,
    dependenceSequenceId: number | Unknown,
  ) => {
    for (const key of Object.keys(params.fields)) {
      if (
        key === 'undefined' ||
        key === undefined ||
        key === null ||
        key === 'null' ||
        key.length === 0
      ) {
        throw new Error(`key is not valid: ${params.fields}`)
      }
    }
    const model: CreateAdvertiseModel = {
      categoryId: params.categoryId,
      userHash: params.userHash!,
      content: params.content.filter((c) => c.title.length > 0),
      price: params.price.length === 0 ? '0' : params.price,
      currency: params.currency!.code,
      fields: params.fields,
      photos: params.photos
        .filter((p) => !!p.hash)
        .map((p) => ({hash: p.hash!, url: p.url!})),
      videos: params.videos
        .filter((p) => p.url.startsWith('https://'))
        .map((p) => ({hash: p.hash!, url: p.url!})),
      location: params.location,
      condition: params.condition,
      isBargainPossible: params.isBargainPossible,
      isSwapPossible: params.isSwapPossible,
      isExclusive: params.isExclusive,
      isVip: params.isVip,
      isTop: params.isTop,
      isFastSale: params.isFastSale,
      isSecureDeal: params.isSecureDeal,
      has360View: false,
      degradation: params.degradation.toString(),
      dependenceSequenceId: !!dependenceSequenceId
        ? dependenceSequenceId
        : undefined,
    }

    if (shouldUpdate) {
      return await this.request.newRequest<{hash: number}>(
        {
          endpoint: `v2/advert/${params.hash}/edit/submit`,
          data: {advert: model},
        },
        dataResponseConverter,
      )
    }

    return await this.request.newRequest<{hash: number}>(
      {
        endpoint: 'v2/draft/submit',
        data: {advertDraft: model, hash: params.hash ?? undefined},
      },
      dataResponseConverter,
    )
  }

  fetchEditAdvertise = async (id: AdvertiseHash) => {
    const response = await this.request.newRequest<CAParamsModel>(
      {
        endpoint: `v2/advert/${id}/edit/load`,
      },
      dataResponseConverter,
    )
    if (response.error) throw new Error(response.error)
    return response
  }

  prepareSaveCreateParams = (params: CAParamsModel) => {
    if (params.data?.screenType === 'array') {
      for (let key of Object.keys(params.arrayTypeFields)) {
        //@ts-ignore
        for (let key1 of Object.keys(params.arrayTypeFields[key])) {
          //@ts-ignore
          params.fields[key1] =
            //@ts-ignore
            params.arrayTypeFields[key][key1]
        }
      }
    }
  }

  prepareParamsToSaveInDrafts = (
    params: CAParamsModel,
    content: Map<string, CAParamContent>,
  ): CAParamsModel => {
    const saveParams = {...params}
    saveParams.content = Array.from(content.values())
    saveParams.photos = params.photos.map((p) => ({
      hash: p.hash,
      url: p.url,
      progress: -1,
      type: 'image',
    }))
    saveParams.videos = params.videos.map((p) => ({
      hash: p.hash,
      url: p.url,
      progress: -1,
      type: 'video',
    }))
    return saveParams
  }
}
